/**
 * SharedOperations — iter-192 UX overhaul.
 *
 * Three sub-views in one file for compactness:
 *   • List (default): all shared operations involving the user, grouped
 *     by month, with stat chips, filters and per-row progress bars.
 *   • Detail (when ``?op=:id`` query string): rich timeline view with
 *     progress indicator + confirm/reject (if destination) OR
 *     reconfirm-mods (if origin + a destination proposed changes).
 *   • Create (when ``?new=1``): visual-kind selector, equal-split
 *     helper, live percentage feedback.
 */
import { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useSpace } from "../contexts/SpaceContext";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Users, ArrowRight, Check, X, AlertCircle, Sparkles,
  Bell, Equal, Calendar as CalendarIcon, ArrowLeftRight,
  TrendingUp, TrendingDown, FileText, UserPlus,
} from "lucide-react";
import { useGroupedByDate } from "../hooks/useGroupedByDate";

// ── Status helpers ─────────────────────────────────────────────────
const STATUS_TONES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  pending_destinations: "bg-amber-50 text-amber-700 border-amber-200",
  pending_origin_fx_reconfirm: "bg-sky-50 text-sky-700 border-sky-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  all_confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  expired: "bg-slate-100 text-slate-600 border-slate-200",
};

const statusLabel = (status, isEs) => {
  const map = isEs
    ? {
        pending: "Pendiente",
        pending_destinations: "Pendientes",
        pending_origin_fx_reconfirm: "Reconfirma FX",
        accepted: "Aceptada",
        all_confirmed: "Confirmada",
        rejected: "Rechazada",
        expired: "Expirada",
      }
    : {
        pending: "Pending",
        pending_destinations: "Pending",
        pending_origin_fx_reconfirm: "FX reconfirm",
        accepted: "Accepted",
        all_confirmed: "Confirmed",
        rejected: "Rejected",
        expired: "Expired",
      };
  return map[status] || status;
};

function StatusChip({ status, isEs }) {
  return (
    <Badge
      className={`${STATUS_TONES[status] || STATUS_TONES.pending} border text-[10px] font-bold uppercase tracking-wider`}
      data-testid={`shared-status-${status}`}
    >
      {statusLabel(status, isEs)}
    </Badge>
  );
}

// ── Kind metadata ──────────────────────────────────────────────────
const KIND_META = {
  expense: {
    icon: TrendingDown,
    color: "rose",
    label: { es: "Egreso", en: "Expense", pt: "Despesa" },
    desc: { es: "Compartido entre workspaces", en: "Shared across workspaces", pt: "Compartilhada entre workspaces" },
  },
  income: {
    icon: TrendingUp,
    color: "emerald",
    label: { es: "Ingreso", en: "Income", pt: "Receita" },
    desc: { es: "Repartido entre workspaces", en: "Split across workspaces", pt: "Repartida entre workspaces" },
  },
  transfer: {
    icon: ArrowLeftRight,
    color: "sky",
    label: { es: "Transferencia", en: "Transfer", pt: "Transferência" },
    desc: { es: "Movimiento interno entre workspaces", en: "Internal movement across workspaces", pt: "Movimento interno entre workspaces" },
  },
  // Legacy keys returned by the backend list endpoint.
  shared_expense: {
    icon: TrendingDown,
    color: "rose",
    label: { es: "Gasto compartido", en: "Shared expense", pt: "Despesa compartilhada" },
    desc: { es: "Compartido entre workspaces", en: "Shared across workspaces", pt: "Compartilhada entre workspaces" },
  },
  internal_transfer: {
    icon: ArrowLeftRight,
    color: "sky",
    label: { es: "Transferencia interna", en: "Internal transfer", pt: "Transferência interna" },
    desc: { es: "Movimiento interno entre workspaces", en: "Internal movement", pt: "Movimento interno" },
  },
  split_income: {
    icon: TrendingUp,
    color: "emerald",
    label: { es: "Ingreso repartido", en: "Split income", pt: "Receita repartida" },
    desc: { es: "Repartido entre workspaces", en: "Split across workspaces", pt: "Repartido entre workspaces" },
  },
};

const kindColorClasses = {
  rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", hover: "hover:border-rose-300 hover:bg-rose-50/50", ring: "ring-rose-300" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", hover: "hover:border-emerald-300 hover:bg-emerald-50/50", ring: "ring-emerald-300" },
  sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", hover: "hover:border-sky-300 hover:bg-sky-50/50", ring: "ring-sky-300" },
};

// iter-193 — Currency options (mirrors the onboarding list for consistency).
const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD — US Dollar" },
  { value: "COP", label: "COP — Peso colombiano" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "MXN", label: "MXN — Peso mexicano" },
  { value: "BRL", label: "BRL — Real brasileño" },
  { value: "ARS", label: "ARS — Peso argentino" },
  { value: "PEN", label: "PEN — Sol peruano" },
  { value: "CLP", label: "CLP — Peso chileno" },
  { value: "GBP", label: "GBP — British Pound" },
];

// ── Helpers ────────────────────────────────────────────────────────
const computeNeedsMyAction = (op, userId) => {
  if (!op || !userId) return false;
  const mySplit = (op.splits || []).find((s) => s.user_id === userId);
  // As destination, pending confirmation.
  if (mySplit && !mySplit.confirmed_at && !mySplit.rejected_at) {
    const stat = op.status;
    if (stat === "pending" || stat === "pending_destinations") return true;
  }
  // As origin, reconfirm pending.
  if (op.owner_user_id === userId) {
    if ((op.splits || []).some((s) => s.modifications_pending_origin)) return true;
  }
  return false;
};

const formatAmount = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const confirmedRatio = (op) => {
  const splits = op.splits || [];
  if (splits.length === 0) return { done: 0, total: 0, pct: 0 };
  const done = splits.filter((s) => s.confirmed_at).length;
  return { done, total: splits.length, pct: Math.round((done / splits.length) * 100) };
};

// ── List view ──────────────────────────────────────────────────────
function SharedList({ isEs, lang, items, currentUserId, onOpen, onCreate }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [onlyMyAction, setOnlyMyAction] = useState(false);

  // Annotate items with my-action flag once.
  const annotated = useMemo(
    () => items.map((op) => ({ ...op, _needsMyAction: computeNeedsMyAction(op, currentUserId) })),
    [items, currentUserId],
  );

  const filtered = annotated.filter((op) => {
    if (onlyMyAction && !op._needsMyAction) return false;
    if (statusFilter !== "all") {
      // Allow both new and legacy status values.
      const matchSet = statusFilter === "pending"
        ? new Set(["pending", "pending_destinations"])
        : statusFilter === "confirmed"
          ? new Set(["accepted", "all_confirmed"])
          : new Set([statusFilter]);
      if (!matchSet.has(op.status)) return false;
    }
    if (kindFilter !== "all" && op.kind !== kindFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const inConcept = (op.concept || "").toLowerCase().includes(q);
      const inEmails = (op.splits || []).some((s) => (s.email || "").toLowerCase().includes(q));
      if (!inConcept && !inEmails) return false;
    }
    return true;
  });

  const counts = annotated.reduce(
    (a, op) => {
      a.total += 1;
      if (op._needsMyAction) a.myAction += 1;
      if (op.status === "pending" || op.status === "pending_destinations") a.pending += 1;
      else if (op.status === "accepted" || op.status === "all_confirmed") a.confirmed += 1;
      return a;
    },
    { total: 0, myAction: 0, pending: 0, confirmed: 0 },
  );

  // Group by month for visual scannability.
  const groups = useGroupedByDate(filtered, "month", (op) => op.date || op.created_at, lang);

  return (
    <Card className="p-4" data-testid="shared-list">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {isEs ? "Operaciones compartidas" : "Shared operations"}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-md">
            {isEs
              ? "Operaciones que involucran a otros workspaces: gastos compartidos, transferencias internas, ingresos repartidos."
              : "Operations involving other workspaces: shared expenses, internal transfers, split incomes."}
          </p>
        </div>
        <Button onClick={onCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="shared-new-btn">
          <Plus className="h-4 w-4 mr-1" /> {isEs ? "Nueva" : "New"}
        </Button>
      </div>

      {/* Stats chips */}
      {counts.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="shared-stats">
          <StatChip
            label={isEs ? "Total" : "Total"} value={counts.total}
            tone="slate"
          />
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={() => setOnlyMyAction((v) => !v)}
            className={`p-2.5 rounded-lg border text-left transition-all ${
              onlyMyAction
                ? "bg-sky-600 border-sky-700 text-white shadow-sm"
                : counts.myAction > 0
                  ? "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"
                  : "bg-slate-50 border-slate-200 text-slate-500"
            }`}
            data-testid="shared-stat-my-action"
            disabled={counts.myAction === 0}
          >
            <div className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${onlyMyAction ? "text-white/90" : ""}`}>
              <Bell className="h-3 w-3" />
              {isEs ? "Tu acción" : "Your action"}
            </div>
            <div className="text-lg font-bold tabular-nums">{counts.myAction}</div>
          </button>
          <StatChip
            label={isEs ? "Pendientes" : "Pending"} value={counts.pending}
            tone="amber"
          />
          <StatChip
            label={isEs ? "Confirmadas" : "Confirmed"} value={counts.confirmed}
            tone="emerald"
          />
        </div>
      )}

      {/* Filters */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,140px,160px] gap-2 mb-4" data-testid="shared-filters">
          <Input
            placeholder={isEs ? "Buscar por concepto o email…" : "Search by concept or email…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
            data-testid="shared-search"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="shared-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isEs ? "Todos los estados" : "All statuses"}</SelectItem>
              <SelectItem value="pending">{isEs ? "Pendientes" : "Pending"}</SelectItem>
              <SelectItem value="confirmed">{isEs ? "Confirmadas" : "Confirmed"}</SelectItem>
              <SelectItem value="pending_origin_fx_reconfirm">{isEs ? "Reconfirma FX" : "FX reconfirm"}</SelectItem>
              <SelectItem value="rejected">{isEs ? "Rechazadas" : "Rejected"}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="shared-kind-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isEs ? "Todos los tipos" : "All kinds"}</SelectItem>
              {["expense", "income", "transfer"].map((k) => {
                const m = KIND_META[k];
                return (
                  <SelectItem key={k} value={k}>
                    {m.label[isEs ? "es" : "en"]}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Empty / no-results */}
      {items.length === 0 && (
        <div className="text-center py-12 px-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/40" data-testid="shared-list-empty">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="font-semibold text-slate-700 text-sm">
            {isEs ? "Aún no tienes operaciones compartidas" : "No shared operations yet"}
          </p>
          <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
            {isEs
              ? "Registra un gasto compartido entre workspaces, una transferencia interna o un ingreso a repartir."
              : "Register a shared expense, an internal transfer, or an income to split across workspaces."}
          </p>
          <Button onClick={onCreate} size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-3.5 w-3.5 mr-1" />
            {isEs ? "Crear primera operación" : "Create first operation"}
          </Button>
        </div>
      )}
      {items.length > 0 && filtered.length === 0 && (
        <div className="text-center py-8 text-sm text-slate-500" data-testid="shared-no-results">
          {isEs ? "Sin resultados para los filtros aplicados." : "No results for the current filters."}
        </div>
      )}

      {/* Grouped rows */}
      <div className="space-y-4">
        {groups.map(({ key, label, items: groupItems }) => (
          <div key={key} data-testid={`shared-group-${key}`}>
            <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-white/95 backdrop-blur-sm">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" />
                {label}
                <span className="text-slate-300">·</span>
                <span className="text-slate-500 normal-case font-semibold">
                  {groupItems.length} {isEs ? (groupItems.length === 1 ? "operación" : "operaciones") : (groupItems.length === 1 ? "operation" : "operations")}
                </span>
              </div>
            </div>
            <div className="space-y-2 mt-1.5">
              {groupItems.map((op) => (
                <SharedRow key={op.id} op={op} isEs={isEs} lang={lang} currentUserId={currentUserId} onOpen={onOpen} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StatChip({ label, value, tone }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-600",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`p-2.5 rounded-lg border ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function SharedRow({ op, isEs, lang, currentUserId, onOpen }) {
  const meta = KIND_META[op.kind] || { icon: FileText, color: "slate", label: { es: op.kind, en: op.kind, pt: op.kind } };
  const Icon = meta.icon;
  const palette = kindColorClasses[meta.color] || kindColorClasses.sky;

  const dateIso = op.date || op.created_at;
  const localDate = dateIso
    ? new Date(dateIso.slice(0, 10) + "T12:00:00Z").toLocaleDateString(
        lang === "en" ? "en-US" : lang === "pt" ? "pt-BR" : "es-ES",
        { day: "numeric", month: "short" },
      )
    : "—";

  const { done, total, pct } = confirmedRatio(op);
  const mySplit = (op.splits || []).find((s) => s.user_id === currentUserId);
  const iAmOrigin = op.owner_user_id === currentUserId;

  return (
    <button /* design-system: ALLOW legacy-todo */
      type="button"
      className={`w-full text-left p-3 rounded-xl border border-slate-200 ${palette.hover} transition-colors flex items-center gap-3 group relative ${op._needsMyAction ? "ring-2 ring-sky-200" : ""}`}
      onClick={() => onOpen(op.id)}
      data-testid={`shared-row-${op.id}`}
    >
      {op._needsMyAction && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-sky-600 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" data-testid={`shared-row-needs-action-${op.id}`}>
          <Bell className="h-2.5 w-2.5" /> {isEs ? "Tú" : "You"}
        </div>
      )}
      <div className={`shrink-0 h-11 w-11 rounded-xl ${palette.bg} ${palette.border} border flex items-center justify-center`}>
        <Icon className={`h-5 w-5 ${palette.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">{op.concept}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>{meta.label[isEs ? "es" : "en"]}</span>
          <span>·</span>
          <span className="tabular-nums">{localDate}</span>
          <span>·</span>
          <span>
            {iAmOrigin
              ? (isEs ? "Eres origen" : "You are origin")
              : mySplit
                ? (isEs ? `Tu parte: ${formatAmount(mySplit.share_amount)} ${op.currency}` : `Your share: ${formatAmount(mySplit.share_amount)} ${op.currency}`)
                : (isEs ? `${total} destino(s)` : `${total} destination(s)`)}
          </span>
        </div>
        {/* Progress bar */}
        {total > 0 && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`}
                style={{ width: `${pct}%` }}
                data-testid={`shared-progress-${op.id}`}
              />
            </div>
            <span className="text-[10px] text-slate-500 tabular-nums font-semibold shrink-0">{done}/{total}</span>
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-bold tabular-nums">{formatAmount(op.total_amount)} {op.currency}</div>
        <div className="mt-1"><StatusChip status={op.status} isEs={isEs} /></div>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0 hidden sm:block" />
    </button>
  );
}

// ── Create form ────────────────────────────────────────────────────
function SharedCreateForm({ isEs, currentUserEmail, onSuccess, onCancel }) {
  const [kind, setKind] = useState("expense");
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [splits, setSplits] = useState([{ email: "", share_amount: "" }]);
  const [notes, setNotes] = useState("");

  const totalAmount = parseFloat(amount) || 0;
  const totalSplits = splits.reduce((sum, s) => sum + (parseFloat(s.share_amount) || 0), 0);
  const sumDiff = totalAmount - totalSplits;
  const sumOk = totalAmount > 0 && Math.abs(sumDiff) < 0.01;
  const sumPct = totalAmount > 0 ? Math.min(100, Math.round((totalSplits / totalAmount) * 100)) : 0;

  const meIsInSplits = splits.some(
    (s) => currentUserEmail && s.email.trim().toLowerCase() === currentUserEmail.toLowerCase(),
  );

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/shared", {
      kind, concept, total_amount: totalAmount, currency, occurred_at: new Date(occurredAt).toISOString(),
      splits: splits.map((s) => ({ email: s.email.trim(), share_amount: parseFloat(s.share_amount) || 0 })),
      notes: notes.trim() || null,
    })).data,
    onSuccess: (op) => {
      toast.success(isEs ? "Operación creada — notificamos a los destinos" : "Operation created — destinations notified");
      onSuccess(op.id);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "create_failed"),
  });

  const canSubmit = concept.trim() && totalAmount > 0 && sumOk
    && splits.every((s) => s.email.trim() && parseFloat(s.share_amount) > 0);

  // Equal split helper
  const distributeEqually = () => {
    if (totalAmount <= 0 || splits.length === 0) return;
    const per = totalAmount / splits.length;
    // Spread rounding remainder on the first row.
    const rounded = per.toFixed(2);
    const remainder = (totalAmount - parseFloat(rounded) * splits.length).toFixed(2);
    const firstAmount = (parseFloat(rounded) + parseFloat(remainder)).toFixed(2);
    setSplits((arr) => arr.map((s, i) => ({ ...s, share_amount: i === 0 ? firstAmount : rounded })));
  };

  // Add the current user as the next destination (or fill the first empty slot).
  const addMyself = () => {
    if (!currentUserEmail || meIsInSplits) return;
    setSplits((arr) => {
      const emptyIdx = arr.findIndex((s) => !s.email.trim());
      if (emptyIdx >= 0) {
        return arr.map((s, i) => i === emptyIdx ? { ...s, email: currentUserEmail } : s);
      }
      return [...arr, { email: currentUserEmail, share_amount: "" }];
    });
  };

  return (
    <Card className="p-4 space-y-5" data-testid="shared-create-form">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {isEs ? "Nueva operación compartida" : "New shared operation"}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {isEs
              ? "Notificaremos a los destinos por email para que confirmen."
              : "Destinations will be notified by email to confirm."}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="shared-create-cancel">{isEs ? "Cancelar" : "Cancel"}</Button>
      </div>

      {/* Visual kind selector */}
      <div>
        <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2 block">{isEs ? "Tipo de operación" : "Operation kind"}</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="shared-kind-cards">
          {["expense", "income", "transfer"].map((k) => {
            const m = KIND_META[k];
            const Icon = m.icon;
            const palette = kindColorClasses[m.color];
            const active = kind === k;
            return (
              <button /* design-system: ALLOW legacy-todo */
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  active
                    ? `${palette.bg} ${palette.border} ${palette.text} shadow-sm ring-2 ${palette.ring} ring-offset-1`
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`shared-kind-card-${k}`}
              >
                <Icon className={`h-5 w-5 mb-1.5 ${active ? palette.text : "text-slate-400"}`} />
                <div className={`text-xs font-bold ${active ? "" : "text-slate-700"}`}>{m.label[isEs ? "es" : "en"]}</div>
                <div className="text-[10px] mt-0.5 leading-tight opacity-75">{m.desc[isEs ? "es" : "en"]}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Concepto" : "Concept"}</Label>
        <Input
          value={concept} onChange={(e) => setConcept(e.target.value)}
          placeholder={isEs ? "Ej: Suscripción Figma" : "e.g. Figma subscription"}
          className="mt-1 h-10" data-testid="shared-concept"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Monto total" : "Total amount"}</Label>
          <Input
            type="number" step="0.01" min="0"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="mt-1 h-10 tabular-nums text-right pr-3" placeholder="0.00"
            data-testid="shared-amount"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Moneda" : "Currency"}</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="mt-1 h-10" data-testid="shared-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Fecha" : "Date"}</Label>
        <Input
          type="date" value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
          className="mt-1 h-10" data-testid="shared-date"
        />
      </div>

      {/* Splits */}
      <div className="border-t border-slate-200 pt-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center">
            <Users className="inline h-3 w-3 mr-1" />
            {isEs ? "Reparto entre workspaces" : "Splits across workspaces"}
          </Label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {currentUserEmail && (
              <Button
                size="sm" variant="outline" type="button"
                onClick={addMyself}
                disabled={meIsInSplits}
                data-testid="shared-add-myself"
                className="h-7 text-xs"
                title={meIsInSplits ? (isEs ? "Ya estás en el reparto" : "You are already in the split") : ""}
              >
                <UserPlus className="h-3 w-3 mr-1" /> {isEs ? "Agregarme" : "Add me"}
              </Button>
            )}
            <Button
              size="sm" variant="outline" type="button"
              onClick={distributeEqually}
              disabled={totalAmount <= 0}
              data-testid="shared-equal-split"
              className="h-7 text-xs"
            >
              <Equal className="h-3 w-3 mr-1" /> {isEs ? "Repartir igual" : "Equal split"}
            </Button>
            <Button
              size="sm" variant="ghost" type="button"
              onClick={() => setSplits((s) => [...s, { email: "", share_amount: "" }])}
              data-testid="shared-add-split"
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" /> {isEs ? "Agregar destino" : "Add destination"}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {splits.map((sp, i) => {
            const sharePct = totalAmount > 0 ? ((parseFloat(sp.share_amount) || 0) / totalAmount) * 100 : 0;
            return (
              <div key={i} className="flex items-center gap-2" data-testid={`shared-split-row-${i}`}>
                <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
                  {i + 1}
                </div>
                <Input
                  type="email"
                  placeholder={isEs ? "email@destino.com" : "email@dest.com"}
                  value={sp.email}
                  onChange={(e) => setSplits((arr) => arr.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))}
                  className="h-9 flex-1"
                  data-testid={`shared-split-email-${i}`}
                />
                <div className="relative">
                  <Input
                    type="number" step="0.01" min="0" placeholder="0.00"
                    value={sp.share_amount}
                    onChange={(e) => setSplits((arr) => arr.map((x, idx) => idx === i ? { ...x, share_amount: e.target.value } : x))}
                    className="h-9 w-28 tabular-nums text-right pr-3"
                    data-testid={`shared-split-amount-${i}`}
                  />
                </div>
                <div className="w-12 text-[10px] tabular-nums text-slate-500 text-right" data-testid={`shared-split-pct-${i}`}>
                  {sharePct > 0 ? `${sharePct.toFixed(0)}%` : "—"}
                </div>
                {splits.length > 1 ? (
                  <Button
                    size="icon" variant="ghost" type="button" className="h-9 w-9 shrink-0"
                    onClick={() => setSplits((arr) => arr.filter((_, idx) => idx !== i))}
                    data-testid={`shared-split-remove-${i}`}
                  ><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                ) : <div className="w-9 shrink-0" />}
              </div>
            );
          })}
        </div>

        {/* Live validation bar */}
        <div className="rounded-lg border border-slate-200 p-2.5 bg-slate-50/40" data-testid="shared-sum-validation">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-slate-700">
              {isEs ? "Suma de repartos" : "Splits sum"}
            </span>
            <span className={`tabular-nums font-bold ${sumOk ? "text-emerald-700" : totalAmount === 0 ? "text-slate-500" : "text-rose-700"}`}>
              {formatAmount(totalSplits)} / {formatAmount(totalAmount)} {currency}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 bg-white rounded-full overflow-hidden border border-slate-200">
            <div
              className={`h-full transition-all ${sumOk ? "bg-emerald-500" : "bg-amber-400"}`}
              style={{ width: `${sumPct}%` }}
            />
          </div>
          {!sumOk && totalAmount > 0 && Math.abs(sumDiff) >= 0.01 && (
            <div className="text-[11px] mt-1.5 text-rose-700 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {sumDiff > 0
                ? (isEs ? `Faltan ${formatAmount(sumDiff)} ${currency}` : `Missing ${formatAmount(sumDiff)} ${currency}`)
                : (isEs ? `Sobran ${formatAmount(-sumDiff)} ${currency}` : `Over by ${formatAmount(-sumDiff)} ${currency}`)}
            </div>
          )}
          {sumOk && (
            <div className="text-[11px] mt-1.5 text-emerald-700 flex items-center gap-1">
              <Check className="h-3 w-3" /> {isEs ? "Las cuotas suman el monto total." : "Splits match the total amount."}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Notas (opcional)" : "Notes (optional)"}</Label>
        <Textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={2} className="mt-1"
          placeholder={isEs ? "Detalles adicionales para los destinos" : "Extra details for destinations"}
          data-testid="shared-notes"
        />
      </div>

      <Button
        onClick={() => createMut.mutate()}
        disabled={!canSubmit || createMut.isPending}
        className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
        data-testid="shared-submit"
      >
        {createMut.isPending
          ? (isEs ? "Creando…" : "Creating…")
          : (isEs ? "Crear y notificar destinos" : "Create and notify destinations")}
      </Button>
    </Card>
  );
}

// ── Detail view ────────────────────────────────────────────────────
function SharedDetail({ isEs, lang, op, currentUserId, spaces, onActed, onBack }) {
  const qc = useQueryClient();
  const isOrigin = op.owner_user_id === currentUserId;
  const mySplit = (op.splits || []).find((s) => s.user_id === currentUserId);
  const myActionable = mySplit && !mySplit.confirmed_at && !mySplit.rejected_at
    && (op.status === "pending" || op.status === "pending_destinations");

  // Destination state for the confirmation form.
  const [chosenSpace, setChosenSpace] = useState(() => mySplit?.chosen_space_id || mySplit?.preferred_space_id || currentUserId);
  const [proposedFxSpot, setProposedFxSpot] = useState("");

  const meta = KIND_META[op.kind] || { icon: FileText, color: "slate", label: { es: op.kind, en: op.kind, pt: op.kind } };
  const Icon = meta.icon;
  const palette = kindColorClasses[meta.color] || kindColorClasses.sky;
  const { done, total, pct } = confirmedRatio(op);

  const confirmMut = useMutation({
    mutationFn: async () => (await api.post(`/shared/${op.id}/confirm`, {
      chosen_space_id: chosenSpace,
      proposed_fx_spot: proposedFxSpot ? parseFloat(proposedFxSpot) : null,
    })).data,
    onSuccess: () => { toast.success(isEs ? "Confirmada" : "Confirmed"); qc.invalidateQueries({ queryKey: ["shared"] }); onActed(); },
    onError: (e) => toast.error(e.response?.data?.detail || "confirm_failed"),
  });
  const rejectMut = useMutation({
    mutationFn: async () => (await api.post(`/shared/${op.id}/reject`, { reason: "" })).data,
    onSuccess: () => { toast.success(isEs ? "Rechazada" : "Rejected"); qc.invalidateQueries({ queryKey: ["shared"] }); onActed(); },
    onError: (e) => toast.error(e.response?.data?.detail || "reject_failed"),
  });
  const reconfirmMut = useMutation({
    mutationFn: async ({ split_id, accept }) => (await api.post(`/shared/${op.id}/origin-reconfirm`, {
      split_id, accept_new_fx: accept,
    })).data,
    onSuccess: () => { toast.success(isEs ? "Decisión enviada" : "Decision sent"); qc.invalidateQueries({ queryKey: ["shared"] }); onActed(); },
    onError: (e) => toast.error(e.response?.data?.detail || "reconfirm_failed"),
  });

  const reconfirmSplits = isOrigin
    ? (op.splits || []).filter((s) => s.modifications_pending_origin)
    : [];

  const dateIso = op.date || op.occurred_at || op.created_at;
  const localDate = dateIso ? new Date(dateIso.slice(0, 10) + "T12:00:00Z").toLocaleDateString(
    lang === "en" ? "en-US" : lang === "pt" ? "pt-BR" : "es-ES",
    { day: "numeric", month: "long", year: "numeric" },
  ) : "—";

  return (
    <Card className="p-0 overflow-hidden" data-testid="shared-detail">
      {/* Header band */}
      <div className={`${palette.bg} ${palette.border} border-b px-4 py-3`}>
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="shared-detail-back" className="-ml-2">
            ← {isEs ? "Volver" : "Back"}
          </Button>
          <StatusChip status={op.status} isEs={isEs} />
        </div>
        <div className="mt-2 flex items-start gap-3">
          <div className={`shrink-0 h-12 w-12 rounded-xl bg-white ${palette.border} border flex items-center justify-center`}>
            <Icon className={`h-6 w-6 ${palette.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-slate-900 truncate">{op.concept}</h2>
            <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span className={`font-semibold ${palette.text}`}>{meta.label[isEs ? "es" : "en"]}</span>
              <span>·</span>
              <span>{localDate}</span>
              {isOrigin && (
                <>
                  <span>·</span>
                  <span className="italic">{isEs ? "Eres origen" : "You are origin"}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{isEs ? "Monto total" : "Total"}</div>
            <div className="text-lg font-bold tabular-nums">{formatAmount(op.total_amount)} {op.currency}</div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Progress bar */}
        {total > 0 && (
          <div data-testid="shared-detail-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {isEs ? "Avance de confirmaciones" : "Confirmations progress"}
              </span>
              <span className="text-xs font-bold text-slate-700 tabular-nums">
                {done} / {total} ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* My pending action banner */}
        {myActionable && (
          <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 flex items-start gap-2" data-testid="shared-detail-my-action-banner">
            <Bell className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" />
            <div className="text-xs text-sky-900 flex-1">
              <strong>{isEs ? "Necesitamos tu confirmación" : "We need your confirmation"}</strong>
              <p className="mt-0.5 text-sky-800">
                {isEs
                  ? `Te asignaron ${formatAmount(mySplit.share_amount)} ${op.currency}. Elige el workspace destino y confirma abajo.`
                  : `You were assigned ${formatAmount(mySplit.share_amount)} ${op.currency}. Pick the target workspace and confirm below.`}
              </p>
            </div>
          </div>
        )}

        {/* Splits */}
        <div>
          <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1">
            <Users className="h-3 w-3" />
            {isEs ? "Reparto" : "Splits"}
          </Label>
          <div className="mt-2 space-y-1.5">
            {(op.splits || []).map((s) => {
              const pctOfTotal = op.total_amount > 0 ? (s.share_amount / op.total_amount) * 100 : 0;
              const isMe = s.user_id === currentUserId;
              return (
                <div
                  key={s.split_id}
                  className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border ${isMe ? "bg-emerald-50/40 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                  data-testid={`shared-detail-split-${s.split_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                      {s.email}
                      {isMe && <Badge className="bg-emerald-600 text-white border-0 text-[9px] uppercase">{isEs ? "Tú" : "You"}</Badge>}
                      {!s.registered && <Badge variant="outline" className="text-[9px] text-slate-400">{isEs ? "No registrado" : "Not registered"}</Badge>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {s.confirmed_at
                        ? <span className="text-emerald-600 font-semibold">✓ {isEs ? "Confirmó" : "Confirmed"}</span>
                        : s.rejected_at
                          ? <span className="text-rose-600 font-semibold">✗ {isEs ? "Rechazó" : "Rejected"}</span>
                          : s.modifications_pending_origin
                            ? <span className="text-sky-600 font-semibold">⏳ {isEs ? "Espera al origen" : "Awaiting origin"}</span>
                            : <span className="text-amber-600 font-semibold">… {isEs ? "Pendiente" : "Pending"}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-bold text-slate-800">{formatAmount(s.share_amount)}</div>
                    <div className="text-[10px] text-slate-500 tabular-nums">{pctOfTotal.toFixed(0)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Destination actions */}
        {myActionable && (
          <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="shared-dest-actions">
            <div>
              <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{isEs ? "Workspace destino *" : "Target workspace *"}</Label>
              <Select value={chosenSpace} onValueChange={setChosenSpace}>
                <SelectTrigger className="mt-1 h-10" data-testid="shared-confirm-space"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(spaces || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.id === currentUserId ? (isEs ? " · Personal" : " · Personal") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">
                {isEs ? "Por defecto tu workspace personal — siempre puedes cambiarlo." : "Defaults to your personal workspace — always changeable."}
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                {isEs ? "Proponer nueva tasa FX (opcional)" : "Propose new FX rate (optional)"}
              </Label>
              <Input
                type="number" step="0.0001" placeholder={isEs ? "Ej: 4310.00" : "e.g. 4310.00"}
                value={proposedFxSpot} onChange={(e) => setProposedFxSpot(e.target.value)}
                className="mt-1 h-10 tabular-nums" data-testid="shared-confirm-fx"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                {isEs ? "Si propones una tasa distinta, el origen deberá aprobarla antes de finalizar." : "If you propose a different rate, the origin must approve it before finalizing."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => confirmMut.mutate()}
                disabled={confirmMut.isPending}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="shared-confirm-btn"
              ><Check className="h-4 w-4 mr-1" /> {isEs ? "Confirmar" : "Confirm"}</Button>
              <Button
                onClick={() => rejectMut.mutate()}
                disabled={rejectMut.isPending}
                variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="shared-reject-btn"
              ><X className="h-4 w-4 mr-1" /> {isEs ? "Rechazar" : "Reject"}</Button>
            </div>
          </div>
        )}

        {/* Origin reconfirm actions */}
        {isOrigin && reconfirmSplits.length > 0 && (
          <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="shared-origin-reconfirm">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-sky-50 border border-sky-200">
              <Sparkles className="h-4 w-4 text-sky-700 shrink-0" />
              <div className="text-xs text-sky-900 flex-1">
                <strong>{isEs ? "Cambios pendientes de tu aprobación" : "Pending modifications to approve"}</strong>
                <div className="mt-1">
                  {isEs
                    ? "Una o más personas modificaron el monto / tipo / tasa FX. Aprueba o mantén los valores originales."
                    : "Destinations proposed changes to amount/kind/FX. Approve the new values or keep the originals."}
                </div>
              </div>
            </div>
            {reconfirmSplits.map((s) => (
              <div key={s.split_id} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200" data-testid={`shared-reconfirm-row-${s.split_id}`}>
                <div className="flex-1 text-xs">
                  <strong>{s.email}</strong>
                  <div className="text-slate-500 mt-0.5">
                    {isEs ? "Original" : "Original"}: {formatAmount(s.share_amount)} {op.currency}
                    {s.share_amount_proposed != null && ` → ${formatAmount(s.share_amount_proposed)}`}
                    {s.fx_proposed_at && ` · FX ${s.fx_proposed_at}`}
                  </div>
                </div>
                <Button
                  size="sm" className="bg-sky-600 hover:bg-sky-700"
                  onClick={() => reconfirmMut.mutate({ split_id: s.split_id, accept: true })}
                  data-testid={`shared-reconfirm-accept-${s.split_id}`}
                >✓ {isEs ? "Aceptar nueva" : "Accept new"}</Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => reconfirmMut.mutate({ split_id: s.split_id, accept: false })}
                  data-testid={`shared-reconfirm-keep-${s.split_id}`}
                >{isEs ? "Mantener original" : "Keep original"}</Button>
              </div>
            ))}
          </div>
        )}

        {op.notes && (
          <div className="border-t border-slate-200 pt-4">
            <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {isEs ? "Notas" : "Notes"}
            </Label>
            <p className="mt-1.5 text-sm text-slate-600 whitespace-pre-wrap p-2.5 rounded-lg bg-slate-50 border border-slate-200">{op.notes}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Page entry ─────────────────────────────────────────────────────
export default function SharedOperations() {
  const { user } = useAuth();
  const { spaces } = useSpace();
  const { lang } = useLang();
  const isEs = lang !== "en";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  void navigate;
  const isCreating = searchParams.get("new") === "1";
  const detailId = searchParams.get("op");

  const listQuery = useQuery({
    queryKey: ["shared"],
    queryFn: async () => (await api.get("/shared")).data,
  });
  const detailQuery = useQuery({
    queryKey: ["shared", detailId],
    queryFn: async () => (await api.get(`/shared/${detailId}`)).data,
    enabled: !!detailId,
  });

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <PageHeader
          icon={<Users className="h-5 w-5" />}
          title={isEs ? "Operaciones compartidas" : "Shared operations"}
          subtitle={isEs
            ? "Registra gastos, ingresos y transferencias que involucran a otros workspaces."
            : "Register expenses, incomes and transfers involving other workspaces."}
        />
        {detailId && detailQuery.data && (
          <SharedDetail
            isEs={isEs} lang={lang} op={detailQuery.data} currentUserId={user?.id}
            spaces={spaces}
            onBack={() => { setSearchParams({}); }}
            onActed={() => detailQuery.refetch()}
          />
        )}
        {!detailId && isCreating && (
          <SharedCreateForm
            isEs={isEs}
            currentUserEmail={user?.email}
            onSuccess={(id) => { setSearchParams({ op: id }); listQuery.refetch(); }}
            onCancel={() => setSearchParams({})}
          />
        )}
        {!detailId && !isCreating && (
          <SharedList
            isEs={isEs} lang={lang}
            items={listQuery.data?.items || []}
            currentUserId={user?.id}
            onCreate={() => setSearchParams({ new: "1" })}
            onOpen={(id) => setSearchParams({ op: id })}
          />
        )}
      </div>
    </Layout>
  );
}
